import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import debug from 'debug';

export const repleteDebug = debug('replete');

/**
 * Connects to multiple data stores.
 * e.g. @subscribe([propName, path, ...argNames])
 * {Object.<string, [string, ...string[]]>}
 * {string} propName The propName used to store the result.
 * {string} path The path of the data store.
 * {string[]} ...propNames List of prop names that will be used to call the
 * data store.
 */
type SubscribeMapping = Record<string, [string, ...string[]]>;

export class DataSource {
    store: Store;
    path: string;

    constructor(store: Store, path: string) {
        this.store = store;
        this.path = path;
    }
    destruct() {}
    key(keyArgs) {
        throw new Error(`'key' method has not been defined.`);
    }
    getInitial(keyArgs) {
        // Return an initial value, maybe kick off an async function to fetch
        // actual data from a server.
        throw new Error(`'getInitial' method has not been defined.`);
    }
    get(keyArgs) {
        return this.store.get(this.path, keyArgs);
    }
    set(...args) {
        return this.store.set(this.path, ...args);
    }
    delete(keyArgs) {
        return this.store.delete(this.path, keyArgs);
    }
}


interface MetaNode {
    children: Record<string, MetaNode>;
    listeners: Function[];
    dataSource: DataSource|null;
}

type Data = Record<string, any>;

interface ResolvedPath {
    dataParent: Data;
    metaParent: MetaNode;
    metaNode: MetaNode;
    dataSegments: any[];
    hasWildcard: boolean;
    lastKey: string;
    attributeSegments: any[];
    dataSource: DataSource;
    path: string;
    keyArgs: any[];
}

export class Store {
    createElement: Function|undefined;
    data: Data|undefined;
    metaTree: MetaNode|undefined;
    pendingPromises: Promise<any>[]|undefined;
    dataSources: DataSource[]|undefined;
    console: Pick<typeof console, 'warn'|'debug'>;

    /**
     * @param {Function} createElement - React, Inferno, os similar library.
     */
    constructor(createElement: Function) {
        this.createElement = createElement;
        this.data = {};
        this.metaTree = this.newMetaNode();
        this.pendingPromises = [];
        this.dataSources = [];
        this.console = console;
    }
    destruct() {
        for (const dataSource of this.dataSources) {
            dataSource.destruct();
        }
        delete this.data;
        delete this.metaTree;
        delete this.createElement;
        delete this.pendingPromises;
        delete this.dataSources;
    }
    newMetaNode(): MetaNode {
        return {
            children: {},
            listeners: [],
            dataSource: null,
        };
    }
    monitor(promise: Promise<any>): Promise<any> {
        let pendingPromises = this.pendingPromises;
        pendingPromises.push(promise);
        // We need to prevent this list from growing forever, it only needs
        // pending promises.
        const remove = () => {
            pendingPromises.splice(pendingPromises.indexOf(promise, 1));
            pendingPromises = [];
        };
        promise.finally(remove);
        return promise;
    }
    register(path: string, DataSourceClass) {
        let metaSegments = path.split('.');

        // Populate trees
        let segment = this.data;

        const lastKey = metaSegments[metaSegments.length - 1];
        metaSegments = metaSegments.slice(0, -1);

        for (const segmentName of metaSegments) {
            segment[segmentName] = segment[segmentName] || {};
            segment = segment[segmentName];
        }

        if (lastKey !== '*') {
            metaSegments.push(lastKey);
        }

        let metaNode = this.metaTree;
        for (const segmentName of metaSegments) {
            metaNode.children[segmentName] = metaNode.children[segmentName] ||
                this.newMetaNode();
            metaNode = metaNode.children[segmentName];
        }
        if (metaNode.dataSource) {
            throw new Error(`A Data Source with this path has already been added: ${path}`);
        }
        metaNode.dataSource = new DataSourceClass(this, path);
        this.dataSources.push(metaNode.dataSource);
    }
    resolvePath(path: string|ResolvedPath, keyArgs: string[]): ResolvedPath {
        if (typeof path !== 'string') {
            // this function was passed an already resolved path.
            return path;
        }

        keyArgs = keyArgs || [];

        const allSegments = path.split('.');
        let lastKey;

        let metaParent = null;
        let metaNode = this.metaTree;
        let dataSource = null;
        let dataSegments = [];
        // TODO: support listening on attributes
        const attributeSegments = [];

        let hasWildcard = false;

        let keySegmentFound = false;

        for (const segmentName of allSegments) {
            if (dataSource) {
                if (!keySegmentFound) {
                    keySegmentFound = true;
                    if (segmentName !== '*') {
                        // if it's a wildcard, leave keyArgs alone. Otherwise,
                        // set keyArgs.
                        if (keyArgs.length) {
                            throw new Error(`'keyArgs' was specified (${keyArgs.length}L), but there was no wildcard after the data source path: '${path}', '${keyArgs}'.`);
                        }
                        lastKey = segmentName;
                        keyArgs = [segmentName];
                    } else {
                        hasWildcard = true;
                    }
                } else {
                    attributeSegments.push(segmentName);
                }
            } else {
                if (!metaNode.children[segmentName]) {
                    break;
                }

                dataSegments.push(segmentName);

                metaParent = metaNode;
                metaNode = metaNode.children[segmentName];

                if (metaNode.dataSource) {
                    dataSource = metaNode.dataSource;
                }
            }
        }

        if (!dataSource) {
            // Not necessarily a problem, this could happen when pre-loading
            // data at the top of the tree on app initialization.
            this.console.debug(`No data source found for '${path}'.`);
            if (hasWildcard) {
                throw new Error(`Wildcard was specified, but no data source was found: '${path}'`);
            }
        }

        if (keyArgs.length === 0) {
            lastKey = dataSegments[dataSegments.length - 1];
            dataSegments = dataSegments.slice(0, dataSegments.length - 1);
        } else if (hasWildcard && keyArgs.length) {
            lastKey = dataSource.key(keyArgs);
        }

        if (dataSource) {
            if (!metaNode.children[lastKey]) {
                metaNode.children[lastKey] = this.newMetaNode();
            }
            metaParent = metaNode;
            metaNode = metaNode.children[lastKey];
        }

        let dataParent = this.data;

        for (const segmentName of dataSegments) {
            if (!Object.hasOwn(dataParent, segmentName)) {
                throw new Error(`Could not resolve data path '${path}'.`);
            }
            dataParent = dataParent[segmentName];
        }

        return {
            dataParent,
            metaParent,
            metaNode,
            dataSegments,
            hasWildcard,
            lastKey,
            attributeSegments,
            dataSource,
            path,
            keyArgs,
        };
    }
    call(path: string, ...otherArgs) {
        let keyArgs = [];
        let methodName;
        let methodArgs = [];

        if (otherArgs.length === 1) {
            [methodName] = otherArgs;
        } else if (otherArgs.length === 2) {
            [keyArgs, methodName] = otherArgs;
        } else {
            [keyArgs, methodName, methodArgs] = otherArgs;
        }

        const resolvedPath = this.resolvePath(path, keyArgs);
        const {dataSource} = resolvedPath;
        keyArgs = resolvedPath.keyArgs;

        if (!dataSource) {
            throw new Error(`No data source was found. ${path}, ${keyArgs}`);
        }

        return dataSource[methodName](keyArgs, methodArgs);
    }
    log() {
        repleteDebug('GLOBAL STATE: ', this.data);
    }
    get(path: string, keyArgs: string[]) {
        const resolvedPath = this.resolvePath(path, keyArgs);
        const {
            dataParent,
            lastKey,
            dataSource,
            attributeSegments,
        } = resolvedPath;
        keyArgs = resolvedPath.keyArgs;

        if (dataParent && Object.hasOwn(dataParent, lastKey)) {
            if (dataSource['clean']) {
                dataParent[lastKey] = dataSource['clean'](dataParent[lastKey]);
            }
            return dataParent[lastKey];
        }

        if (!dataSource) {
            throw new Error(`No data source was found. ${path}, ${keyArgs}`);
        }

        // return unsaved initial data
        let val = dataSource.getInitial(keyArgs);

        for (const attr of attributeSegments) {
            val = val[attr];
        }
        return val;
    }
    set(path: string, ...args) {
        // repleteDebug(`STORE set() path: ${path}, args: ${args}`);
        // TODO: support TTL, TTL after unlisten
        if (args.length === 1) {
            args.unshift([]);
        }
        const [keyArgs, val] = args;
        const resolvedPath = this.resolvePath(path, keyArgs);

        let {dataParent, lastKey, attributeSegments} = resolvedPath;

        if (process.env.NODE_ENV !== 'production') {
            try {
                // TODO: add deep checking
                if (val !== JSON.parse(JSON.stringify(val))) {
                    // this.console.warn(`deserialized val does not equal val.`,
                    //     resolvedPath, val);
                }
            } catch (e) {
                // this.console.warn(`val was not JSON serializable.`,
                //     resolvedPath, val);
            }
        }

        const allSegments = [lastKey].concat(attributeSegments);
        lastKey = allSegments.pop()!;
        for (const attr of allSegments) {
            dataParent = dataParent[attr];
        }
        dataParent[lastKey] = val;

        // TODO: Check if there's any child listeners and make sure they are
        // invalidated if needed.
        this.callListeners(resolvedPath, val);
    }
    refresh(path: string, keyArgs: string[]) {
        const resolvedPath = this.resolvePath(path, keyArgs);
        const {dataParent, lastKey} = resolvedPath;
        keyArgs = resolvedPath.keyArgs;
        delete dataParent[lastKey];

        this.callListeners(resolvedPath, this.get(path, keyArgs));
    }
    delete(path: string, keyArgs: string[]) {
        const resolvedPath = this.resolvePath(path, keyArgs);
        const {dataParent, lastKey} = resolvedPath;
        keyArgs = resolvedPath.keyArgs;
        delete dataParent[lastKey];
    }
    clearDataSource(path: string) {
        const resolvedPath = this.resolvePath(path, []);
        const {dataParent, lastKey} = resolvedPath;
        for (const key of Object.keys(dataParent[lastKey])) {
            delete dataParent[lastKey][key];
        }
    }
    callListeners(resolvedPath: ResolvedPath, val) {
        const {metaNode} = resolvedPath;

        // We need to use a copy of the metaNode.listeners array, because a
        // listener can trigger a chain of events that results in
        // metaNode.listeners being updated in place (when a callback is
        // unlistened), which can cause subsequent listeners to be skipped and
        // new listeners to be triggered instead.
        metaNode?.listeners.slice().forEach((listener: Function) => {
            listener(resolvedPath, val);
        });
    }
    onChange(path: string, keyArgs: string[], callback: Function) {
        const resolvedPath = this.resolvePath(path, keyArgs);
        const {metaNode} = resolvedPath;

        const listenersArray = metaNode.listeners;
        listenersArray.push(callback);

        return () => {
            const idx = listenersArray.indexOf(callback);
            if (idx > -1) {
                listenersArray.splice(idx, 1);
            }
        };
    }
}

function wrap(f: Function, props: jsx.Props, context: jsx.AppGlobals) {
    return f({...props}, context);
}

/**
 * Transforms properties.
 */
export const transformProps = (f: Function): Function => {
    // Used for calculating/cleaning props from router params.
    return function transformPropsInner(Target): Function {
        function TransformPropsWrapper(
            props: jsx.Props, context: jsx.AppGlobals) {
            props = wrap(f, props, context);
            return context.store.createElement(Target, props);
        }
        Object.defineProperty(TransformPropsWrapper, 'name', {
            value: `TransformPropsWrapper(${Target.name})`,
            writable: false,
        });
        return TransformPropsWrapper;
    };
};


/**
 * Gives the passed function a chance to return a Component instead of the
 * wrapped component.
 */
export const maybeReturnComponent = (f: Function): Function => {
    return function maybeReturnComponentInner(Target) {
        function MaybeReturnComponentWrapper(props: jsx.Props,
            context: jsx.AppGlobals) {
            const NewTarget = wrap(f, props, context) || Target;
            return context.store.createElement(NewTarget, props);
        }
        Object.defineProperty(MaybeReturnComponentWrapper, 'name', {
            value: `MaybeReturnComponentWrapper(${Target.name})`,
            writable: false,
        });
        return MaybeReturnComponentWrapper;
    };
};


function getKeyArgsFromPropNames(propNames: string[], props: jsx.Props,
    state: jsx.State): string[] {
    if (propNames.length && typeof propNames[0] === 'function') {
        return (propNames[0] as Function)(props, state);
    }
    return propNames.map((propName) => {
        return props[propName];
    });
}

export const subscribe = (mappings: SubscribeMapping): Function => {
    // In case we want to allow a transformProps function as the first arg.
    // let f = null;
    // if (args.length > 1) {
    //     f = args.shift();
    // }
    // const mappings = args[0];
    return function subscribeInner(Target) {
        const name = `SubscribeWrapper(${
            Object.keys(mappings).join(',')}:${Target.name})`;
        const targetDebug = repleteDebug.extend('Target').extend(name);

        class SubscribeWrapper extends Component<jsx.Props, jsx.State> {
            mounted: boolean;
            unlistenArray: Function[];
            pendingMappings: SubscribeMapping;
            keyArgsMap: Record<string, string[]>;

            constructor(props: jsx.Props, context: jsx.AppGlobals) {
                super(props, context);
                // targetDebug('constructor', Target);
                // For some unknown reason, in tsc strict mode we have to
                // manually instantiate `this.state` on this Component but not
                // others.
                this.state = {};
                this.mounted = false;
                this.unlistenArray = [];
                this.pendingMappings = {};
                this.keyArgsMap = {};
                this.init(props);
            }
            componentWillUnmount() {
                this.mounted = false;
                this.unlistenAll();
            }
            componentWillUpdate(nextProps: jsx.Props, nextState: jsx.State) {
                // targetDebug('componentWillUpdate', nextProps, nextState);
                this.init(nextProps);
                this.addListeners(nextProps, mappings);
                this.addListeners(this.props, this.pendingMappings);
                this.pendingMappings = {};
            }
            componentDidMount() {
                // targetDebug('componentDidMount');
                this.mounted = true;
                this.addListeners(this.props, mappings);
                this.addListeners(this.props, this.pendingMappings);
                this.pendingMappings = {};
            }
            init(props: jsx.Props) {
                // targetDebug('init');
                this.pendingMappings = {};

                this.unlistenAll();

                this.propsToState(props, mappings);
                // NOTE: even if addListeners is called here, setState still
                // doesn't update/trigger a new render.
            }
            addListeners(props: jsx.Props, map: SubscribeMapping): Function[] {
                const store = this.context.store;

                const listenerMapper = (
                    [propName, [path, ...propNames]]: Entry<SubscribeMapping>
                ) => {
                    // get path, pass in props in propMap
                    const keyArgs = this.keyArgsMap[propName];

                    // on update, call setState
                    const onChangeHandler = (
                        resolvedPath: ResolvedPath, val) => {
                        if (this.mounted) {
                            this.setState({[propName]: val});
                        } else {
                            this.state = {
                                ...this.state,
                                [propName]: val,
                            };
                        }
                    };
                    const unlistener = store.onChange(
                        path, keyArgs, onChangeHandler);
                    this.unlistenArray.push(unlistener);
                    return unlistener;
                };
                return Object.entries(map).map(listenerMapper);
            }
            unlistenAll() {
                if (this.unlistenArray) {
                    this.unlistenArray.forEach((unlisten: Function) => {
                        unlisten();
                    });
                }
                this.unlistenArray = [];
            }
            propsToState(props: jsx.Props, map: SubscribeMapping): jsx.State {
                const store = this.context.store;

                // TODO: support promises for getKeyArgsFromPropNames functions?
                const stateMapper = (
                    [propName, [path, ...propNames]]: Entry<SubscribeMapping>
                ) => {
                    // get path, pass in props in propMap
                    const keyArgs = getKeyArgsFromPropNames(
                        propNames, props, this.state);
                    this.keyArgsMap[propName] = keyArgs;
                    const val = store.get(path, keyArgs);
                    return [propName, val];
                };
                const newState = Object.fromEntries(Object.entries(map).map(
                    stateMapper));
                // If this.state isn't updated in place, it causes weird
                // errors, components won't get rerendered. Not sure why.
                Object.assign(this.state!, newState);
                return newState;
            }
            subscribe(map: SubscribeMapping): jsx.State {
                targetDebug('subscribe', map);
                const vals = this.propsToState(this.props, map);

                this.pendingMappings = {...this.pendingMappings, ...map};

                return vals;
            }
            render() {
                const props: jsx.Props = {...this.props};

                for (const [key, val] of Object.entries(this.state || {})) {
                    props[key] = val;
                }

                props.subscribe = this.subscribe.bind(this);

                return this.context.store.createElement(Target, props);
            }
        }
        Object.defineProperty(SubscribeWrapper, 'name', {
            value: name,
            writable: false,
        });
        return SubscribeWrapper;
    };
};

